import React from "react";
import styled from "styled-components";

import {
  DRAWER_SCOPE_ORDER,
  DRAWER_SCOPE_LABEL,
} from "./captureDrawerScopes";
import { formatChipCount } from "../Capture.helpers";
// SHARED capture styleds (composed, never hand-rolled — G-21). The same tab
// styled the Add-capture modal already uses, so the pane does not introduce a
// second, drifting tab look.
import { TypeTabs, TypeTab, ChipCount } from "../styles";

/**
 * MEASURED FIX (2026-09-08, web-stg main.2168c7e3.js, 820x1180): this strip
 * is mounted embedded inside the builder's side-pane slot
 * (SegmentBuilder/index.jsx `secondDivStyles`), which is `30vw` wide — 246px
 * at exactly 820px — not the standalone Drawer's fixed 380px. TypeTabs' own
 * nowrap-except-mobilePortrait default (`device.mobilePortrait` needs
 * max-width:768px, which 820 never satisfies) left the two tabs demanding
 * 263px in one row, 17px past the 246px pane (confirmed by the same
 * `aside[data-testid=captured-on-wheel-drawer]` scrollWidth/clientWidth
 * check the bug report used). TypeTabs itself is shared with the composer's
 * 6-tab row and the media-type filter row, both of which rely on staying
 * nowrap at this width (see the Aug 6 fix note in ../styles.js) — so the
 * override lives on a subclass used ONLY by this two-tab strip, not on the
 * shared component. `flex-wrap: wrap` is content-driven (a browser only
 * wraps when a row doesn't fit), so it is a no-op everywhere the strip
 * already fits, e.g. the standalone 380px Drawer or this same pane at
 * 1440/1024.
 */
const ScopeTabsRow = styled(TypeTabs)`
  flex-wrap: wrap;
`;

/**
 * CaptureDrawerScopeTabs — the builder Capture pane's two sections (QA-77):
 * the captures assigned to the wheel being edited, and the owner's whole
 * Capture library.
 *
 * It is a real ARIA tablist, not two styled buttons: the pane swaps the list
 * below it, which is precisely what a tablist announces. Arrow keys move
 * between tabs (the WAI-ARIA tabs pattern) so the section can be changed
 * without a pointer.
 *
 * Each tab carries its section's TOTAL. When that total is not yet verified the
 * badge says so ("N+" / "…") rather than printing a page-scoped number as if it
 * were the real one — see captureDrawerScopes.scopeCount for the measured
 * defect that rule comes from.
 *
 * Props:
 *   scope     the active DRAWER_SCOPE
 *   onScope   (scope) => void
 *   counts    { [scope]: { value, complete } }
 */
const CaptureDrawerScopeTabs = ({ scope, onScope, counts = {} }) => {
  const handleKeyDown = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const index = DRAWER_SCOPE_ORDER.indexOf(scope);
    const delta = e.key === "ArrowRight" ? 1 : -1;
    const next =
      DRAWER_SCOPE_ORDER[
        (index + delta + DRAWER_SCOPE_ORDER.length) % DRAWER_SCOPE_ORDER.length
      ];
    onScope?.(next);
  };

  return (
    <ScopeTabsRow
      role="tablist"
      aria-label="Which captures to show"
      data-testid="capture-scope-tabs"
      onKeyDown={handleKeyDown}
    >
      {DRAWER_SCOPE_ORDER.map((id) => {
        const active = scope === id;
        const { value = 0, complete = true } = counts[id] || {};
        return (
          <TypeTab
            key={id}
            type="button"
            role="tab"
            id={`capture-scope-tab-${id}`}
            aria-selected={active}
            aria-controls="captured-on-wheel-body"
            // Roving tabindex: one stop for the whole strip, arrows move within.
            tabIndex={active ? 0 : -1}
            active={active}
            onClick={() => onScope?.(id)}
            data-testid={`capture-scope-tab-${id}`}
          >
            {DRAWER_SCOPE_LABEL[id]}
            <ChipCount active={active} data-testid={`capture-scope-count-${id}`}>
              {formatChipCount(value, { complete })}
            </ChipCount>
          </TypeTab>
        );
      })}
    </ScopeTabsRow>
  );
};

export default CaptureDrawerScopeTabs;
